import React, { Component } from 'react'
import cx from 'classnames'
import { isFunction } from 'lodash'
import PropTypes from 'prop-types'
import Tooltip from 'rc-tooltip'
import { placements } from 'rc-tooltip/lib/placements'

import { placementPropType } from './tooltip-prop-type'

import './tooltip.module.css'

const TRANSITIONS = {
    bottom: 'updown',
    bottomLeft: 'updown',
    bottomRight: 'updown',
    left: 'rightleft',
    leftBottom: 'rightleft',
    leftTop: 'rightleft',
    right: 'leftright',
    rightBottom: 'leftright',
    rightTop: 'leftright',
    top: 'downup',
    topLeft: 'downup',
    topRight: 'downup'
}

const propTypes = {
    animate: PropTypes.bool,
    className: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
    getTooltipContainer: PropTypes.func,
    manualTrigger: PropTypes.bool,
    matchWidth: PropTypes.bool,
    mouseEnterDelay: PropTypes.number,
    mouseLeaveDelay: PropTypes.number,
    onVisibleChange: PropTypes.func,
    placement: placementPropType,
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    timeout: PropTypes.number,
    triggerOnClick: PropTypes.bool,
    triggerOnFocus: PropTypes.bool,
    triggerOnHover: PropTypes.bool,
    useStandardAnimation: PropTypes.bool,
    visible: PropTypes.bool
}

const defaultProps = {
    animate: true,
    manualTrigger: false,
    matchWidth: false,
    mouseEnterDelay: 0.35,
    mouseLeaveDelay: 0,
    placement: 'top',
    timeout: 0,
    triggerOnClick: false,
    triggerOnFocus: true,
    triggerOnHover: true,
    useStandardAnimation: true,
    visible: false
}

/**
 * This is a tooltip wrapper/implementation for t3-widgets. It is based on an MIT-licensed rc-tooltip library.
 * @see {@link https://github.com/react-component/tooltip}
 *
 * Molecule Tooltip, UX specs:
 * @see {@link https://confluence.tomtomgroup.com/display/TTB/Molecules#Molecules-Tooltip}
 *
 * @example <caption>Basic example</caption>
 * <code>
 * <Tooltip content={<span>I am a tooltip</span>}>
 *     <div>I am the target, hover over me</div>
 * </Tooltip>
 * </code>
 *
 * @example <caption>Usage with custom components</caption>
 * Case 1: we have complete control over the custom component that is targeted
 * In this case make sure that the custom element passes all the props
 * to its first child. Otherwise the tooltip will never appear.
 *
 * <code>
 * function Tester (props) {
 *     const { children, ...otherProps } = props
 *     return (
 *         <div style={{color: 'red', display: 'inline-block'}} {...otherProps}>
 *             {children}
 *         </div>
 *     )
 * }
 *
 * <Tooltip content={<span>I am a tooltip</span>}>
 *     <Tester>I am the target, hover over me</Tester>
 * </Tooltip>
 * </code>
 *
 * Case 2: we have no possibility to change the custom component. In this case try to target a html5-child-element
 * <code>
 * <Button>
 *     <Tooltip ...>
 *         <span>Button Text</span>
 *     </Tooltip>
 * </Button>
 * </code>
 *
 * Please note, that when using a button inside a tooltip and this button can be disabled,
 * the tooltip won't work properly because the browsers don't handle all mouse events on
 * disabled buttons. Therefore you can use the TooltipButton component.
 * @see TooltipButton
 *
 * @property {Object} props.content - Contents to be rendered inside of the tooltip
 * @property {boolean} [props.animate=true] - Should there be an animation when the tooltip appears/disappears
 * @property {string} [props.className] - Class name to set for the tooltip element
 * @property {boolean} [props.manualTrigger=false] - Disables automatic triggers and enables manual use of show/hide
 * @property {number} [props.mouseEnterDelay=0.3] - Delay time to show when mouse enters (in SECONDS!)
 * @property {number} [props.mouseLeaveDelay=0.1] - Delay time to hide when mouse leaves (in SECONDS!)
 * @property {boolean} [props.matchWidth=false] - Tries to set the width of the tooltip the same as target's width
 * @property {Function} [props.onVisibleChange] - Callback to fire, when the visibility of the tooltip changes
 * @property {Function} [props.getTooltipContainer] - Callback to provide the container element for the tooltip
 * @property {string} [props.placement='top'] - Where the tooltip should preferrably be placed (4 ways supported)
 * @property {Object} [props.style] - Additional style to set on the tooltip element
 * @property {number} [props.timeout=0] - If > 0, then will show the tooltip once for as many milliseconds
 * @property {boolean} [props.triggerOnClick=false] - The tooltip appears when mouse clicks on the target
 * @property {boolean} [props.triggerOnFocus=true] - The tooltip appears when the target element gets the focus
 * @property {boolean} [props.triggerOnHover=true] - The tooltip appears when mouse hovers over the target
 * @property {boolean} [props.useStandardAnimation=true] - When off, (buggy) placement-dependent animation is used
 * @property {boolean} [props.visible=false] - Tooltip visible on start (only works if manualTrigger=false)
 */
class T3Tooltip extends Component {
    constructor(props) {
        super(props)

        this.show = this.show.bind(this)
        this.hide = this.hide.bind(this)
        this.trigger = this.trigger.bind(this)
        this.cloneChild = this.cloneChild.bind(this)

        this.state = {
            style: {},
            visible: false
        }
        this.childNodes = []
        this.timers = {}
    }

    componentDidMount() {
        this.applyStyle()
        this.showOnce(this.props)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.showOnce(nextProps)
    }

    componentWillUnmount() {
        Object.values(this.timers).forEach(function (timerId) {
            clearTimeout(timerId)
        })
    }

    render() {
        const {
            animate,
            children,
            className,
            content: overlay,
            getTooltipContainer,
            manualTrigger,
            mouseEnterDelay,
            mouseLeaveDelay,
            onVisibleChange,
            placement,
            timeout,
            triggerOnClick,
            triggerOnFocus,
            triggerOnHover,
            useStandardAnimation,
            visible: defaultVisible
        } = this.props
        const { style: overlayStyle, visible } = this.state
        const overlayClassName = cx(className, 't3sel-tooltip')
        const transition = useStandardAnimation ? 'zoom' : TRANSITIONS[placement]

        const triggerArray = []
        if (!manualTrigger && timeout === 0) {
            if (triggerOnHover) {
                triggerArray.push('hover')
            }
            if (triggerOnClick) {
                triggerArray.push('click')
            }
            if (triggerOnFocus) {
                triggerArray.push('focus')
            }
        }
        this.childNodes = []

        const otherProps = {}
        if (manualTrigger || timeout > 0) {
            otherProps.visible = visible
        }

        return (
            <Tooltip
                {...{
                    align: { offset: placements[placement].offset },
                    defaultVisible,
                    destroyTooltipOnHide: { keepParent: false },
                    getTooltipContainer,
                    mouseEnterDelay,
                    mouseLeaveDelay,
                    onVisibleChange,
                    overlay,
                    overlayClassName,
                    overlayStyle,
                    placement,
                    transitionName: animate ? `rc-tooltip-${transition}` : null,
                    trigger: triggerArray
                }}
                {...otherProps}
            >
                {React.Children.map(children, this.cloneChild)[0]}
            </Tooltip>
        )
    }

    /**
     * Makes the tooltip visible. If timeout is passed && timeout > 0, the tooltip is hidden afterwards
     * Requires the manualTrigger property to be set to true (automatic triggering is then switched off).
     *
     * @private
     * @param {number} [timeout=0] - After how many milliseconds the tooltip needs to disappear
     * @returns {void}
     */
    show(timeout = 0) {
        this.trigger(true)

        if (timeout > 0) {
            clearTimeout(this.timers.hide)
            this.timers.hide = setTimeout(() => {
                this.hide()
                delete this.timers.hide
            }, timeout)
        }
    }

    /**
     * Hides the tooltip after being shown with show(). Requires the manualTrigger property
     * to be set to true (automatic triggering is then switched off).
     *
     * @private
     * @returns {void}
     */
    hide() {
        this.trigger(false)
    }

    /**
     * @private
     * @param {boolean} visible - The visibility state to be set
     * @returns {void}
     */
    trigger(visible) {
        if (!this.props.manualTrigger && this.props.timeout === 0) {
            console.warn('Tooltip: Set manualTrigger property to true in order to use show() or hide() or use timeout')
            return
        }
        this.setState({
            visible
        })
    }

    /**
     * Simple callback for React.Children.map to save refs in a class variable.
     *
     * @private
     * @param {ReactElement} element - Current element processed by map function
     * @param {number} idx - Current elements index
     * @returns {ReactElement}
     */
    cloneChild(element, idx) {
        if (element.prototype instanceof Component) {
            return React.cloneElement(element, {
                ref: ref => {
                    this.childNodes[idx] = ref
                }
            })
        }
        return element
    }

    /**
     * @private
     * @param {Object} props - Current or nextProps depending on lifecycle call
     * @returns {void}
     */
    showOnce(props) {
        const { timeout } = props

        if (timeout > 0) {
            clearTimeout(this.timers.show)
            this.timers.show = setTimeout(() => {
                this.show(timeout)
                delete this.timers.show
            })
        }
    }

    /**
     * @private
     * @returns {void}
     */
    applyStyle() {
        if (!this.hasChildNodes()) {
            return
        }

        // Need to do an instant timeout, to ensure the rendered component has already got a width
        clearTimeout(this.timers.applyStyle)
        this.timers.applyStyle = setTimeout(() => {
            if (!this.hasChildNodes()) {
                return
            }
            const width = this.childNodes[0].getBoundingClientRect().width
            if (this.props.matchWidth && this.state.style.width !== width) {
                this.setState({
                    style: Object.assign(
                        {
                            maxWidth: width,
                            width: width
                        },
                        this.props.style
                    )
                })
            }
            delete this.timers.applyStyle
        })
    }

    /**
     * @private
     * @returns {boolean}
     */
    hasChildNodes() {
        return this.childNodes.length && this.childNodes[0] && isFunction(this.childNodes[0].getBoundingClientRect)
    }
}

T3Tooltip.propTypes = propTypes
T3Tooltip.defaultProps = defaultProps

export default T3Tooltip
